import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import WatchLiveButton from '@/components/WatchLiveButton';
import { Tournament, Team } from '@/types';
import {
  getTournaments,
  getTournament,
  getMatchesByTournament,
  getTeams,
  getParticipatingTeams,
  getPointsTable,
  getPlayerStars,
  getBroadcastSettings,
  getClubSettings,
  slugifyPlayerName,
  supabase,
  PlayerStarRow,
  PointsTableRow,
} from '@/lib/supabase';
import PublicLiveScoreboard from '@/components/PublicLiveScoreboard';
import { capture } from '@/lib/analytics';

type TabId = 'live' | 'standings' | 'players' | 'stats';

interface LiveMatch {
  id: string;
  matchNumber: number;
  matchType: 'league' | 'playoff';
  status: string;
  date: string;
  matchTime: string | null;
  teamA: Team | null;
  teamB: Team | null;
  teamAId: string;
  teamBId: string;
  winnerId: string | null;
  startTime: string | null;
  liveStreamUrl: string | null;
  matchLabel: string | null;
  sets: Array<{
    id: string;
    setNumber: number;
    scoreA: number;
    scoreB: number;
    winnerId: string | null;
    endedAt: string | null;
    currentServingTeamId: string | null;
    currentServerId: string | null;
  }>;
}

function formatDuration(startTime: string | null, endTime: string | null): string {
  if (!startTime) return '—';
  const start = new Date(startTime).getTime();
  const end = endTime ? new Date(endTime).getTime() : Date.now();
  const totalSecs = Math.floor((end - start) / 1000);
  if (totalSecs < 0) return '—';
  const hours = Math.floor(totalSecs / 3600);
  const mins = Math.floor((totalSecs % 3600) / 60);
  const secs = totalSecs % 60;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}:${String(secs).padStart(2, '0')}`;
}

function formatTime(time: string | null): string {
  if (!time) return '—';
  const [h, m] = time.split(':');
  const hour = parseInt(h);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${m} ${ampm}`;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function isMatchLive(status: string): boolean {
  return status === 'live' || status === 'set-won' || status === 'first-server';
}

function isScheduled(status: string): boolean {
  return status === 'scheduled';
}

export default function PublicTournamentCentre() {
  const { tournamentId: urlTournamentId, matchId } = useParams<{ tournamentId?: string; matchId?: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const showAll = searchParams.get('showAll') === 'true';

  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [selectedTournamentId, setSelectedTournamentId] = useState<string | null>(urlTournamentId || null);
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [clubLogo, setClubLogo] = useState<string | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [liveMatches, setLiveMatches] = useState<LiveMatch[]>([]);
  const [standings, setStandings] = useState<PointsTableRow[]>([]);
  const [playerStats, setPlayerStats] = useState<PlayerStarRow[]>([]);
  const [expandedTeamIds, setExpandedTeamIds] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<TabId>('live');
  const [matchesSubTab, setMatchesSubTab] = useState<'live' | 'scheduled' | 'completed'>('live');
  const userPickedSubTab = useRef(false);
  const [playerCategory, setPlayerCategory] = useState<'total' | 'F' | 'A' | 'D' | 'L' | 'S' | 'B'>('total');
  const [searchQuery, setSearchQuery] = useState('');
  const [teamFilter, setTeamFilter] = useState<string>('all');
  const [loading, setLoading] = useState(true);

  const livePollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const statsPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Stable refs so the poll setInterval closure always calls the latest version
  // without needing to re-create the interval every time the callback changes.
  const refreshLiveDataRef = useRef<(tid: string) => Promise<void>>(async () => {});
  const refreshStatsDataRef = useRef<(tid: string) => Promise<void>>(async () => {});

  // Auto-select the best default sub-tab on first load only.
  // Once the user manually picks a sub-tab, never override it.
  useEffect(() => {
    if (userPickedSubTab.current) return;
    const hasLive = liveMatches.some(m => isMatchLive(m.status));
    const hasScheduled = liveMatches.some(m => isScheduled(m.status));
    if (hasLive) setMatchesSubTab('live');
    else if (hasScheduled) setMatchesSubTab('scheduled');
    else setMatchesSubTab('completed');
  }, [liveMatches]);

  const allPlayersMap = useMemo(() => {
    const map = new Map<string, string>();
    teams.forEach(t => t.players.forEach(p => map.set(p.id, p.name)));
    return map;
  }, [teams]);

  const matchStats = useMemo(() => {
    const completed = liveMatches.filter(m => m.status === 'match-won').length;
    const live = liveMatches.filter(m => m.status === 'live' || m.status === 'set-won').length;
    const pending = liveMatches.filter(m => m.status === 'scheduled' || m.status === 'setup' || m.status === 'first-server').length;
    const totalSets = liveMatches.reduce((sum, m) => sum + (m.sets?.length || 0), 0);
    const totalPoints = liveMatches.reduce((sum, m) => {
      return sum + m.sets.reduce((sSum, s) => sSum + s.scoreA + s.scoreB, 0);
    }, 0);
    const teamIds = new Set<string>();
    liveMatches.forEach(m => {
      teamIds.add(m.teamAId);
      teamIds.add(m.teamBId);
    });
    return {
      total: liveMatches.length,
      completed,
      live,
      pending,
      teams: teamIds.size,
      players: teams.reduce((sum, t) => sum + t.players.length, 0),
      sets: totalSets,
      points: totalPoints,
    };
  }, [liveMatches, teams]);

  const topTeam = useMemo(() => {
    if (standings.length === 0) return null;
    return standings[0];
  }, [standings]);

  const topPlayer = useMemo(() => {
    if (playerStats.length === 0) return null;
    const sorted = [...playerStats].sort((a, b) => b.totalStars - a.totalStars);
    return sorted[0];
  }, [playerStats]);

  const filteredPlayers = useMemo(() => {
    let filtered = [...playerStats];

    if (teamFilter !== 'all') {
      filtered = filtered.filter(p => p.teamId === teamFilter);
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(p => p.playerName.toLowerCase().includes(q) || p.teamName.toLowerCase().includes(q));
    }

    const sortKey = playerCategory === 'total' ? 'totalStars'
      : playerCategory === 'F' ? 'finisherStars'
      : playerCategory === 'A' ? 'acerStars'
      : playerCategory === 'D' ? 'defenderStars'
      : playerCategory === 'L' ? 'liberoStars'
      : playerCategory === 'S' ? 'setterStars'
      : 'blockerStars';

    filtered.sort((a, b) => b[sortKey as keyof PlayerStarRow] as number - (a[sortKey as keyof PlayerStarRow] as number));

    return filtered;
  }, [playerStats, teamFilter, searchQuery, playerCategory]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [tournamentsData, broadcastSettings, clubSettings] = await Promise.all([
          getTournaments(),
          getBroadcastSettings().catch(() => null),
          getClubSettings().catch(() => ({})),
        ]);
        setTournaments(tournamentsData);
        setClubLogo((clubSettings as Record<string, string>)['club_logo_url'] || null);

        if (urlTournamentId) {
          setSelectedTournamentId(urlTournamentId);
        } else if (tournamentsData.length > 0) {
          const defaultTournamentId = broadcastSettings?.defaultTournamentId;
          if (defaultTournamentId && tournamentsData.some((t: any) => t.id === defaultTournamentId)) {
            setSelectedTournamentId(defaultTournamentId);
          } else {
            const liveTournament = tournamentsData.find((t: any) => {
              const now = new Date();
              const start = new Date(t.startDate);
              const end = new Date(t.endDate);
              return now >= start && now <= end;
            });
            setSelectedTournamentId(liveTournament?.id || tournamentsData[0].id);
          }
        }
      } catch (err) {
        console.error('Failed to load initial data:', err);
      }
    };
    loadData();
  }, [urlTournamentId]);

  useEffect(() => {
    if (!selectedTournamentId) return;
    const loadTournament = async () => {
      setLoading(true);
      try {
        const [tournamentData, rawTeamsData, participatingTeams] = await Promise.all([
          getTournament(selectedTournamentId),
          getTeams(),
          getParticipatingTeams(selectedTournamentId),
        ]);
        setTournament(tournamentData);
        const teamsData: Team[] = (rawTeamsData ?? []).map((t: any) => ({
          id: t.id,
          name: t.name,
          shortName: t.short_name,
          color: t.color,
          logoUrl: t.logo_url,
          players: (t.players || []).map((p: any) => ({ id: p.id, name: p.name, number: p.number, teamId: p.team_id })),
        }));
        setTeams(participatingTeams.length > 0 ? participatingTeams : teamsData);
        await refreshLiveData(selectedTournamentId);
      } catch (err) {
        console.error('Failed to load tournament:', err);
      } finally {
        setLoading(false);
      }
    };
    loadTournament();
  }, [selectedTournamentId]);

  const refreshLiveData = useCallback(async (tid: string) => {
    try {
      const matchesData = await getMatchesByTournament(tid);
      const teamMap = new Map(teams.map(t => [t.id, t]));

      const formattedMatches: LiveMatch[] = matchesData.map((m: any) => ({
        id: m.id,
        matchNumber: m.match_number,
        matchType: m.match_type,
        status: m.status,
        date: m.date,
        matchTime: m.match_time,
        teamA: teamMap.get(m.team_a_id) || null,
        teamB: teamMap.get(m.team_b_id) || null,
        teamAId: m.team_a_id,
        teamBId: m.team_b_id,
        winnerId: m.winner_id,
        startTime: m.start_time,
        liveStreamUrl: m.live_stream_url ?? null,
        matchLabel: m.match_label ?? null,
        sets: (m.sets || []).map((s: any) => ({
          id: s.id,
          setNumber: s.set_number,
          scoreA: s.score_a,
          scoreB: s.score_b,
          winnerId: s.winner_id,
          endedAt: s.ended_at,
          currentServingTeamId: s.current_serving_team_id ?? null,
          currentServerId: s.current_server_id ?? null,
        })),
      }));

      setLiveMatches(formattedMatches);
    } catch (err) {
      console.error('Failed to refresh live data:', err);
    }
  }, [teams]);

  const refreshStatsData = useCallback(async (tid: string) => {
    try {
      const [standingsData, playersData] = await Promise.all([
        getPointsTable(tid),
        getPlayerStars(tid),
      ]);
      setStandings(standingsData);
      setPlayerStats(playersData);
    } catch (err) {
      console.error('Failed to refresh stats:', err);
    }
  }, []);

  useEffect(() => {
    if (tournament?.name) {
      document.title = `Volleyball - ${tournament.name}`;
    }
    return () => { document.title = 'Volleyball Scorer'; };
  }, [tournament?.name]);

  // Keep refs in sync with latest callbacks so poll closures don't go stale
  useEffect(() => { refreshLiveDataRef.current = refreshLiveData; }, [refreshLiveData]);
  useEffect(() => { refreshStatsDataRef.current = refreshStatsData; }, [refreshStatsData]);

  useEffect(() => {
    if (!selectedTournamentId) return;
    refreshLiveData(selectedTournamentId);
    refreshStatsData(selectedTournamentId);
  }, [selectedTournamentId, refreshLiveData, refreshStatsData]);

  useEffect(() => {
    if (!selectedTournamentId) return;

    // Realtime: fires when any match row for this tournament changes
    // (status, winner, score snapshot). Triggers a full live-data refetch.
    const channel = supabase
      .channel(`tournament-matches-${selectedTournamentId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'matches', filter: `tournament_id=eq.${selectedTournamentId}` },
        () => { refreshLiveDataRef.current(selectedTournamentId); },
      )
      .subscribe();

    // Fallback poll — catches set-score updates which live on the `sets` table
    // (not on `matches`) and therefore don't trigger the realtime subscription.
    livePollRef.current = setInterval(() => {
      if (!document.hidden) refreshLiveDataRef.current(selectedTournamentId);
    }, 30000);

    statsPollRef.current = setInterval(() => {
      if (!document.hidden) refreshStatsDataRef.current(selectedTournamentId);
    }, 30000);

    return () => {
      supabase.removeChannel(channel);
      if (livePollRef.current) clearInterval(livePollRef.current);
      if (statsPollRef.current) clearInterval(statsPollRef.current);
    };
  }, [selectedTournamentId]);

  const handleTournamentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const tid = e.target.value;
    setSelectedTournamentId(tid);
    navigate(`/live/${tid}`, { replace: true });
    capture('live_page_tournament_changed', { tournament_id: tid });
  };

  const handleMatchClick = (match: LiveMatch) => {
    navigate(`/live/${selectedTournamentId}/match/${match.id}`);
  };

  const getCategoryLabel = (cat: string): string => {
    switch (cat) {
      case 'total': return 'MVP';
      case 'F': return 'FINISHER';
      case 'A': return 'ACER';
      case 'D': return 'DEFENDER';
      case 'L': return 'LIBERO';
      case 'S': return 'SETTER';
      case 'B': return 'BLOCKER';
      default: return cat;
    }
  };

  const getCategoryIcon = (cat: string): string => {
    switch (cat) {
      case 'total': return '★';
      case 'F': return '🏐';
      case 'A': return '⚡';
      case 'D': return '🛡';
      case 'L': return '🛡';
      case 'S': return '🎯';
      case 'B': return '🧱';
      default: return '•';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <span className="w-8 h-8 border-2 border-text-muted border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (matchId) {
    return <PublicLiveScoreboard matchId={matchId} tournamentId={selectedTournamentId!} />;
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Sticky Header + Tabs — wrapped together so tabs always sit below the header
          regardless of whether the selector second-row is shown */}
      <div className="sticky top-0 z-40">

        {/* Header */}
        <div className="bg-surface border-b border-border">
          <div className="max-w-4xl mx-auto px-4 py-2">
          {/* Single row: Club logo + dropdown (shrunk) + Throwball link */}
            <div className="flex items-center gap-2">
              {clubLogo && (
                <img src={clubLogo} alt="Club" className="w-8 h-8 rounded-lg object-contain flex-shrink-0" />
              )}
              {showAll && tournaments.length > 1 ? (
                <select
                  value={selectedTournamentId || ''}
                  onChange={handleTournamentChange}
                  className="flex-1 min-w-0 text-sm bg-surface-raised border border-border rounded-lg px-2 py-1.5 font-semibold text-text-primary cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  {tournaments.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              ) : (
                <h1 className="flex-1 min-w-0 text-sm font-bold text-text-primary truncate">{tournament?.name || 'Tournament'}</h1>
              )}
              <a
                href={`${window.location.origin.replace(/volleyball/i, 'throwball')}/live`}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-warning/10 text-warning hover:bg-warning/20 transition-colors font-bold text-xs leading-none whitespace-nowrap flex-shrink-0"
                title="View Throwball live scores"
              >
                Throwball
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                  <polyline points="15 3 21 3 21 9" />
                  <line x1="10" y1="14" x2="21" y2="3" />
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Navigation Tabs — all 5 always visible, equal width */}
        <div className="bg-surface border-b border-border">
          <nav className="max-w-4xl mx-auto flex">
            {(['live', 'standings', 'players', 'stats'] as TabId[]).map(tab => (
              <button
                key={tab}
                onClick={() => {
                  setActiveTab(tab);
                  capture('live_page_tab_changed', { tab, tournament_id: selectedTournamentId });
                }}
                className={`flex-1 flex flex-col items-center gap-1.5 px-1 py-2 sm:py-3 text-[10px] sm:text-xs font-bold uppercase tracking-wide transition-colors border-b-2 ${
                  activeTab === tab
                    ? 'text-primary border-primary'
                    : 'text-text-secondary border-transparent hover:text-text-primary'
                }`}
              >
                <span className="text-sm sm:text-base leading-none">
                  {tab === 'live' && '🏐'}
                  {tab === 'standings' && '🏆'}
                  {tab === 'players' && '🌟'}
                  {tab === 'stats' && (
                    <svg className="w-4 h-4 sm:w-[18px] sm:h-[18px] inline-block" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 3v18h18" />
                      <path d="M18 17V9" />
                      <path d="M13 17V5" />
                      <path d="M8 17v-4" />
                    </svg>
                  )}
                </span>
                <span className="leading-none">
                  {tab === 'live' && 'Matches'}
                  {tab === 'standings' && 'Table'}
                  {tab === 'players' && 'Stars'}
                  {tab === 'stats' && 'Stats'}
                </span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 py-4">
          {/* MATCHES TAB — Live / Scheduled / Completed sub-tabs */}
          {activeTab === 'live' && (() => {
            const liveOnly = liveMatches
              .filter(m => isMatchLive(m.status))
              .sort((a, b) => {
                const dateA = a.date || '9999-99-99';
                const dateB = b.date || '9999-99-99';
                if (dateA !== dateB) return dateA.localeCompare(dateB);
                const timeA = a.matchTime || '99:99';
                const timeB = b.matchTime || '99:99';
                return timeA.localeCompare(timeB);
              });
            const scheduledOnly = liveMatches
              .filter(m => isScheduled(m.status))
              .sort((a, b) => {
                const dateA = a.date || '9999-99-99';
                const dateB = b.date || '9999-99-99';
                if (dateA !== dateB) return dateA.localeCompare(dateB);
                const timeA = a.matchTime || '99:99';
                const timeB = b.matchTime || '99:99';
                return timeA.localeCompare(timeB);
              });
            const completedOnly = liveMatches
              .filter(m => m.status === 'match-won')
              .sort((a, b) => {
                const dateA = a.date || '0000-00-00';
                const dateB = b.date || '0000-00-00';
                if (dateA !== dateB) return dateB.localeCompare(dateA);
                const timeA = a.matchTime || '00:00';
                const timeB = b.matchTime || '00:00';
                return timeB.localeCompare(timeA);
              });
            const subTabMatches = matchesSubTab === 'live' ? liveOnly : matchesSubTab === 'scheduled' ? scheduledOnly : completedOnly;

            return (
              <div className="space-y-3">
                {/* Sub-tabs */}
                <div className="flex gap-1.5">
                  <MatchesSubTabButton label="Live" count={liveOnly.length} active={matchesSubTab === 'live'} onClick={() => { userPickedSubTab.current = true; setMatchesSubTab('live'); }} />
                  <MatchesSubTabButton label="Upcoming" count={scheduledOnly.length} active={matchesSubTab === 'scheduled'} onClick={() => { userPickedSubTab.current = true; setMatchesSubTab('scheduled'); }} />
                  <MatchesSubTabButton label="Completed" count={completedOnly.length} active={matchesSubTab === 'completed'} onClick={() => { userPickedSubTab.current = true; setMatchesSubTab('completed'); }} />
                </div>

                {subTabMatches.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-4xl mb-3">{matchesSubTab === 'completed' ? '✅' : '🏐'}</div>
                    <p className="text-text-secondary text-sm">
                      {matchesSubTab === 'live' && 'No live matches right now'}
                      {matchesSubTab === 'scheduled' && 'No upcoming matches'}
                      {matchesSubTab === 'completed' && 'No completed matches yet'}
                    </p>
                    <p className="text-text-muted text-xs mt-1">Check Upcoming Matches!</p>
                  </div>
                ) : matchesSubTab === 'scheduled' ? (
                  subTabMatches.map(match => {
                    const isTBDA = !match.teamA || match.teamA.shortName === 'TBD';
                    const isTBDB = !match.teamB || match.teamB.shortName === 'TBD';
                    return (
                    <div
                      key={match.id}
                      className="w-full bg-surface border border-border rounded-xl p-4"
                    >
                      {/* Match Header */}
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-bold text-text-secondary">MATCH #{match.matchNumber}</span>
                        <div className="flex items-center gap-2">
                          {match.matchLabel ? (
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-warning/20 text-warning uppercase">{match.matchLabel}</span>
                          ) : match.matchType === 'playoff' ? (
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-warning/20 text-warning">PLAYOFF</span>
                          ) : (
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-info/20 text-info">LEAGUE</span>
                          )}
                          <span className="px-2 py-0.5 rounded bg-surface-raised text-text-secondary text-[10px] font-bold uppercase">
                            UPCOMING
                          </span>
                        </div>
                      </div>

                      {/* Teams */}
                      <div className="flex items-center justify-between gap-4">
                        {/* Team A */}
                        <div className="flex-1 flex items-center gap-3">
                          <div className="flex flex-col items-center gap-1 min-w-[60px]">
                            {isTBDA ? (
                              <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-surface-raised border border-border">
                                <span className="text-text-muted font-bold text-sm">?</span>
                              </div>
                            ) : match.teamA?.logoUrl ? (
                              <img src={match.teamA.logoUrl} alt={match.teamA.name} loading="lazy" className="w-12 h-12 rounded-lg object-contain bg-surface-raised p-1" />
                            ) : (
                              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: match.teamA?.color }}>
                                <span className="text-white font-bold text-lg">{match.teamA?.shortName?.charAt(0)}</span>
                              </div>
                            )}
                            <span className={`text-xs font-bold truncate max-w-[80px] ${isTBDA ? 'text-text-muted italic' : ''}`} style={isTBDA ? undefined : { color: match.teamA?.color }}>
                              {isTBDA ? 'TBD' : match.teamA?.shortName}
                            </span>
                          </div>
                        </div>

                        {/* VS */}
                        <div className="flex flex-col items-center gap-1 flex-shrink-0">
                          <span className="text-lg font-bold text-text-secondary">VS</span>
                        </div>

                        {/* Team B */}
                        <div className="flex-1 flex items-center gap-3 flex-row-reverse">
                          <div className="flex flex-col items-center gap-1 min-w-[60px]">
                            {isTBDB ? (
                              <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-surface-raised border border-border">
                                <span className="text-text-muted font-bold text-sm">?</span>
                              </div>
                            ) : match.teamB?.logoUrl ? (
                              <img src={match.teamB.logoUrl} alt={match.teamB.name} loading="lazy" className="w-12 h-12 rounded-lg object-contain bg-surface-raised p-1" />
                            ) : (
                              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: match.teamB?.color }}>
                                <span className="text-white font-bold text-lg">{match.teamB?.shortName?.charAt(0)}</span>
                              </div>
                            )}
                            <span className={`text-xs font-bold truncate max-w-[80px] ${isTBDB ? 'text-text-muted italic' : ''}`} style={isTBDB ? undefined : { color: match.teamB?.color }}>
                              {isTBDB ? 'TBD' : match.teamB?.shortName}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Date / Time */}
                      <div className="flex items-center justify-center mt-3 pt-3 border-t border-border text-xs">
                        <span className="text-text-secondary font-medium">
                          {formatDate(match.date)}{match.matchTime ? ` · ${formatTime(match.matchTime)}` : ''}
                        </span>
                      </div>
                    </div>
                    );
                  }))
                  : matchesSubTab === 'completed' ? (
                  subTabMatches.map(match => {
                    const teamASetsWon = match.sets.filter(s => s.winnerId === match.teamAId).length;
                    const teamBSetsWon = match.sets.filter(s => s.winnerId === match.teamBId).length;
                    const winnerIsA = match.winnerId === match.teamAId;
                    const winnerIsB = match.winnerId === match.teamBId;
                    const finalScoreA = match.sets.reduce((sum, s) => sum + (s.scoreA ?? 0), 0);
                    const finalScoreB = match.sets.reduce((sum, s) => sum + (s.scoreB ?? 0), 0);

                    return (
                      <div
                        key={match.id}
                        onClick={() => handleMatchClick(match)}
                        className="w-full bg-surface border border-border rounded-xl p-4 hover:border-text-muted transition-colors text-left cursor-pointer"
                      >
                        {/* Match Header */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-text-secondary">MATCH #{match.matchNumber}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {match.matchType === 'playoff' && (
                              <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-warning/20 text-warning">PLAYOFF</span>
                            )}
                            {match.matchType === 'league' && (
                              <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-info/20 text-info">LEAGUE</span>
                            )}
                          </div>
                        </div>

                        {/* Teams + Final Score */}
                        <div className="flex items-center justify-between gap-2 overflow-hidden">
                          {/* Team A */}
                          <div className="flex-1 flex items-center gap-2 min-w-0">
                            <div className="flex flex-col items-center gap-1 flex-shrink-0">
                              {match.teamA?.logoUrl ? (
                                <img src={match.teamA.logoUrl} alt={match.teamA.name} loading="lazy" className={`w-10 h-10 rounded-lg object-contain bg-surface-raised p-1 ${winnerIsA ? '' : 'opacity-60'}`} />
                              ) : match.teamA ? (
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${winnerIsA ? '' : 'opacity-60'}`} style={{ backgroundColor: match.teamA.color }}>
                                  <span className="text-white font-bold">{match.teamA.shortName?.charAt(0)}</span>
                                </div>
                              ) : null}
                              <span className="text-xs font-bold" style={{ color: match.teamA?.color }}>
                                {match.teamA?.shortName}
                              </span>
                            </div>
                            <div className="text-center">
                              <p className={`font-mono-nums text-4xl font-bold ${winnerIsA ? 'text-text-primary' : 'text-text-muted'}`}>
                                {finalScoreA}
                              </p>
                              <div className="flex gap-1 justify-center mt-1">
                                {[...Array(match.matchType === 'playoff' ? 5 : 3)].map((_, i) => {
                                  const wonSet = match.sets.find(s => s.setNumber === i + 1)?.winnerId === match.teamAId;
                                  return (
                                    <span
                                      key={i}
                                      className={`w-2 h-2 rounded-full ${wonSet ? '' : 'bg-border'}`}
                                      style={wonSet ? { backgroundColor: match.teamA?.color } : {}}
                                    />
                                  );
                                })}
                              </div>
                            </div>
                          </div>

                          {/* VS / Sets */}
                          <div className="flex flex-col items-center gap-1 flex-shrink-0">
                            <span className="text-[10px] font-bold text-text-secondary">FT</span>
                            <span className="text-lg font-bold text-text-secondary">{teamASetsWon} - {teamBSetsWon}</span>
                            <span className="text-[10px] text-text-secondary">SETS</span>
                          </div>

                          {/* Team B */}
                          <div className="flex-1 flex items-center gap-2 flex-row-reverse min-w-0">
                            <div className="flex flex-col items-center gap-1 flex-shrink-0">
                              {match.teamB?.logoUrl ? (
                                <img src={match.teamB.logoUrl} alt={match.teamB.name} loading="lazy" className={`w-10 h-10 rounded-lg object-contain bg-surface-raised p-1 ${winnerIsB ? '' : 'opacity-60'}`} />
                              ) : match.teamB ? (
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${winnerIsB ? '' : 'opacity-60'}`} style={{ backgroundColor: match.teamB.color }}>
                                  <span className="text-white font-bold">{match.teamB.shortName?.charAt(0)}</span>
                                </div>
                              ) : null}
                              <span className="text-xs font-bold" style={{ color: match.teamB?.color }}>
                                {match.teamB?.shortName}
                              </span>
                            </div>
                            <div className="text-center">
                              <p className={`font-mono-nums text-4xl font-bold ${winnerIsB ? 'text-text-primary' : 'text-text-muted'}`}>
                                {finalScoreB}
                              </p>
                              <div className="flex gap-1 justify-center mt-1">
                                {[...Array(match.matchType === 'playoff' ? 5 : 3)].map((_, i) => {
                                  const wonSet = match.sets.find(s => s.setNumber === i + 1)?.winnerId === match.teamBId;
                                  return (
                                    <span
                                      key={i}
                                      className={`w-2 h-2 rounded-full ${wonSet ? '' : 'bg-border'}`}
                                      style={wonSet ? { backgroundColor: match.teamB?.color } : {}}
                                    />
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Winner + Date */}
                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-border text-xs">
                          <span className="text-text-secondary font-medium">
                            {winnerIsA && match.teamA && (
                              <span className="flex items-center gap-1">
                                🏆 <span style={{ color: match.teamA.color }}>{match.teamA.name}</span>
                              </span>
                            )}
                            {winnerIsB && match.teamB && (
                              <span className="flex items-center gap-1">
                                🏆 <span style={{ color: match.teamB.color }}>{match.teamB.name}</span>
                              </span>
                            )}
                          </span>
                          <span className="text-text-secondary">
                            {formatDate(match.date)}{match.matchTime ? ` ${formatTime(match.matchTime)}` : ''}
                          </span>
                        </div>

                        {match.liveStreamUrl && (
                          <WatchLiveButton url={match.liveStreamUrl} isCompleted onClick={(e) => e.stopPropagation()} />
                        )}
                      </div>
                    );
                  })
                ) : (
                  subTabMatches.map(match => {
                    const currentSet = match.sets.find(s => !s.endedAt) || match.sets[match.sets.length - 1];
                    const teamASetsWon = match.sets.filter(s => s.winnerId === match.teamAId).length;
                    const teamBSetsWon = match.sets.filter(s => s.winnerId === match.teamBId).length;
                    const servingTeamId = currentSet?.currentServingTeamId ?? null;
                    const servingTeam = servingTeamId
                      ? (servingTeamId === match.teamAId ? match.teamA : match.teamB)
                      : null;
                    const serverName = currentSet?.currentServerId
                      ? allPlayersMap.get(currentSet.currentServerId) ?? null
                      : null;

                    // Live match card (clickable)
                    return (
                      <div
                        key={match.id}
                        onClick={() => handleMatchClick(match)}
                        className="w-full bg-surface border border-border rounded-xl p-4 hover:border-text-muted transition-colors text-left cursor-pointer"
                      >
                        {/* Match Header */}
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-xs font-bold text-text-muted">MATCH #{match.matchNumber}</span>
                          <div className="flex items-center gap-2">
                            {match.matchType === 'playoff' && (
                              <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-warning/20 text-warning">PLAYOFF</span>
                            )}
                            <span className="flex items-center gap-1.5 px-2 py-0.5 rounded bg-success/20 text-success">
                              <span className="w-2 h-2 rounded-full bg-success animate-pulse" />
                              <span className="text-[10px] font-bold uppercase">LIVE</span>
                            </span>
                          </div>
                        </div>

                        {/* Teams */}
                        <div className="flex items-center justify-between gap-4">
                          {/* Team A */}
                          <div className="flex-1 flex items-center gap-3">
                            <div className="flex flex-col items-center gap-1 min-w-[60px]">
                              {match.teamA?.logoUrl ? (
                                <img src={match.teamA.logoUrl} alt={match.teamA.name} loading="lazy" className="w-12 h-12 rounded-lg object-contain bg-surface-raised p-1" />
                              ) : match.teamA ? (
                                <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: match.teamA.color }}>
                                  <span className="text-white font-bold text-lg">{match.teamA.shortName?.charAt(0)}</span>
                                </div>
                              ) : null}
                              <span className="text-xs font-bold truncate max-w-[80px]" style={{ color: match.teamA?.color }}>
                                {match.teamA?.shortName}
                              </span>
                            </div>
                            <div className="text-center">
                              <p className="font-mono-nums text-4xl font-bold text-text-primary">
                                {currentSet?.scoreA ?? 0}
                              </p>
                              <div className="flex gap-1 justify-center mt-1">
                                {[...Array(match.matchType === 'playoff' ? 5 : 3)].map((_, i) => {
                                  const wonSet = match.sets.find(s => s.setNumber === i + 1)?.winnerId === match.teamAId;
                                  return (
                                    <span
                                      key={i}
                                      className={`w-2 h-2 rounded-full ${wonSet ? '' : 'bg-border'}`}
                                      style={wonSet ? { backgroundColor: match.teamA?.color } : {}}
                                    />
                                  );
                                })}
                              </div>
                            </div>
                          </div>

                          {/* VS */}
                          <div className="flex flex-col items-center gap-1 flex-shrink-0">
                            <span className="text-xs font-bold text-text-muted">SET {currentSet?.setNumber || 1}</span>
                            <span className="text-lg font-bold text-text-muted">VS</span>
                            <span className="text-[10px] text-text-muted">{teamASetsWon} - {teamBSetsWon}</span>
                          </div>

                          {/* Team B */}
                          <div className="flex-1 flex items-center gap-3 flex-row-reverse">
                            <div className="flex flex-col items-center gap-1 min-w-[60px]">
                              {match.teamB?.logoUrl ? (
                                <img src={match.teamB.logoUrl} alt={match.teamB.name} loading="lazy" className="w-12 h-12 rounded-lg object-contain bg-surface-raised p-1" />
                              ) : match.teamB ? (
                                <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: match.teamB.color }}>
                                  <span className="text-white font-bold text-lg">{match.teamB.shortName?.charAt(0)}</span>
                                </div>
                              ) : null}
                              <span className="text-xs font-bold truncate max-w-[80px]" style={{ color: match.teamB?.color }}>
                                {match.teamB?.shortName}
                              </span>
                            </div>
                            <div className="text-center">
                              <p className="font-mono-nums text-4xl font-bold text-text-primary">
                                {currentSet?.scoreB ?? 0}
                              </p>
                              <div className="flex gap-1 justify-center mt-1">
                                {[...Array(match.matchType === 'playoff' ? 5 : 3)].map((_, i) => {
                                  const wonSet = match.sets.find(s => s.setNumber === i + 1)?.winnerId === match.teamBId;
                                  return (
                                    <span
                                      key={i}
                                      className={`w-2 h-2 rounded-full ${wonSet ? '' : 'bg-border'}`}
                                      style={wonSet ? { backgroundColor: match.teamB?.color } : {}}
                                    />
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Match Info */}
                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-border text-xs">
                          <span className="text-text-muted">
                            {servingTeam ? (
                              <span className="flex items-center gap-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
                                Serving: <span style={{ color: servingTeam.color }}>{servingTeam.shortName}</span>
                                {serverName && (
                                  <span className="text-text-secondary ml-1">{serverName}</span>
                                )}
                              </span>
                            ) : null}
                          </span>
                          <span className="text-text-muted font-mono-nums">
                            {formatDuration(match.startTime, null)}
                          </span>
                        </div>
                        {match.liveStreamUrl && (
                          <WatchLiveButton url={match.liveStreamUrl} onClick={(e) => e.stopPropagation()} />
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            );
          })()}

          {activeTab === 'standings' && (
            <div className="space-y-2">
              {standings.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-4xl mb-3">🏆</div>
                  <p className="text-text-secondary text-sm">No standings available yet</p>
                  <p className="text-text-muted text-xs mt-1">Matches need to be completed first</p>
                </div>
              ) : (
                standings.map((team) => {
                  const isExpanded = expandedTeamIds.has(team.teamId);

                  return (
                    <div key={team.teamId} className="bg-surface border border-border rounded-xl overflow-hidden">
                      <button
                        onClick={() => setExpandedTeamIds(prev => {
                          const next = new Set(prev);
                          next.has(team.teamId) ? next.delete(team.teamId) : next.add(team.teamId);
                          return next;
                        })}
                        className="w-full p-3 flex items-center gap-2 hover:bg-surface-raised transition-colors"
                      >
                        {/* Team — flex-1, name coloured with team colour */}
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          {team.teamLogoUrl ? (
                            <img
                              src={team.teamLogoUrl}
                              alt={team.teamName}
                              loading="lazy"
                              className="w-7 h-7 rounded-full object-cover flex-shrink-0"
                              onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                            />
                          ) : (
                            <span className="w-5 h-5 rounded-full flex-shrink-0" style={{ backgroundColor: team.teamColor }} />
                          )}
                          <span
                            className="font-bold text-sm leading-tight"
                            style={{ color: team.teamColor || undefined }}
                          >
                            {team.teamName}
                          </span>
                        </div>

                        {/* W / L / PTS — only essentials in collapsed view */}
                        <div className="flex items-center gap-2 text-xs flex-shrink-0">
                          <div className="text-center w-7">
                            <p className="font-bold text-sky-400">{team.played}</p>
                            <p className="text-text-secondary text-[10px] font-medium">P</p>
                          </div>
                          <div className="text-center w-7">
                            <p className="font-bold text-success">{team.wins}</p>
                            <p className="text-text-secondary text-[10px] font-medium">W</p>
                          </div>
                          <div className="text-center w-7">
                            <p className="font-bold text-error">{team.losses}</p>
                            <p className="text-text-secondary text-[10px] font-medium">L</p>
                          </div>
                          <div className="text-center w-9">
                            <p className="font-bold text-primary">{team.points}</p>
                            <p className="text-text-secondary text-[10px] font-medium">PTS</p>
                          </div>
                        </div>

                        {/* Expand Arrow */}
                        <svg
                          className={`w-4 h-4 text-text-muted transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`}
                          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                        >
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </button>

                      {/* Expanded Details */}
                      {isExpanded && (() => {
                        const setNet = team.setsWon - team.setsLost;
                        const totalPts = team.pointsScored + team.pointsConceded;
                        const pointsPct = totalPts > 0 ? (team.pointsScored / totalPts * 100).toFixed(0) : '0';
                        return (
                          <div className="px-4 pb-4 pt-0 border-t border-border bg-surface-raised/50">
                            {/* Row 1: Sets */}
                            <div className="grid grid-cols-4 gap-3 pt-3">
                              <div>
                                <p className="text-[10px] text-text-secondary uppercase">Sets Won</p>
                                <p className="text-sm font-bold text-success">{team.setsWon}</p>
                              </div>
                              <div>
                                <p className="text-[10px] text-text-secondary uppercase">Sets Lost</p>
                                <p className="text-sm font-bold text-error">{team.setsLost}</p>
                              </div>
                              <div>
                                <p className="text-[10px] text-text-secondary uppercase">Set Net</p>
                                <p className={`text-sm font-bold ${setNet >= 0 ? 'text-success' : 'text-error'}`}>
                                  {setNet >= 0 ? `+${setNet}` : setNet}
                                </p>
                              </div>
                              <div>
                                <p className="text-[10px] text-text-secondary uppercase">SET%</p>
                                <p className="text-sm font-bold text-text-primary">{team.setWinPct}%</p>
                              </div>
                            </div>
                            {/* Row 2: Points */}
                            <div className="grid grid-cols-4 gap-3 pt-3">
                              <div>
                                <p className="text-[10px] text-text-secondary uppercase">Pts Won</p>
                                <p className="text-sm font-bold text-success">{team.pointsScored}</p>
                              </div>
                              <div>
                                <p className="text-[10px] text-text-secondary uppercase">Pts Lost</p>
                                <p className="text-sm font-bold text-error">{team.pointsConceded}</p>
                              </div>
                              <div>
                                <p className="text-[10px] text-text-secondary uppercase">Pts Net</p>
                                <p className={`text-sm font-bold ${team.netPoints >= 0 ? 'text-success' : 'text-error'}`}>
                                  {team.netPoints >= 0 ? `+${team.netPoints}` : team.netPoints}
                                </p>
                              </div>
                              <div>
                                <p className="text-[10px] text-text-secondary uppercase">PTS%</p>
                                <p className="text-sm font-bold text-text-primary">{pointsPct}%</p>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* PLAYERS TAB */}
          {activeTab === 'players' && (
            <div className="space-y-4">
              {/* Category Tabs */}
              <div className="flex gap-1 overflow-x-auto hide-scrollbar pb-2">
                {(['total', 'F', 'A', 'D', 'L', 'S', 'B'] as const).map(cat => (
                  <button
                    key={cat}
                    onClick={() => setPlayerCategory(cat)}
                    className={`flex-shrink-0 px-3 py-2 rounded-lg text-xs font-bold uppercase transition-colors ${
                      playerCategory === cat
                        ? 'bg-primary text-white'
                        : 'bg-surface border border-border text-text-secondary hover:border-text-secondary'
                    }`}
                  >
                    {getCategoryIcon(cat)} {getCategoryLabel(cat)}
                  </button>
                ))}
              </div>

              {/* Filters */}
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Search player..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="flex-1 px-3 py-2 bg-surface border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
                <select
                  value={teamFilter}
                  onChange={e => setTeamFilter(e.target.value)}
                  className="px-3 py-2 bg-surface border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  <option value="all">All Teams</option>
                  {teams.map(t => (
                    <option key={t.id} value={t.id}>{t.shortName}</option>
                  ))}
                </select>
              </div>

              {/* Leaderboard */}
              <div className="bg-surface border border-border rounded-xl overflow-hidden">
                {filteredPlayers.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-text-secondary text-sm">No players found</p>
                  </div>
                ) : (
                  filteredPlayers.map((player, idx) => {
                    const isTopThree = idx < 3;
                    const sortKey = playerCategory === 'total' ? 'totalStars'
                      : playerCategory === 'F' ? 'finisherStars'
                      : playerCategory === 'A' ? 'acerStars'
                      : playerCategory === 'D' ? 'defenderStars'
                      : playerCategory === 'L' ? 'liberoStars'
                      : playerCategory === 'S' ? 'setterStars'
                      : 'blockerStars';
                    const catValue = player[sortKey as keyof PlayerStarRow] as number;

                    return (
                      <div
                        key={player.playerId}
                        className={`flex items-center gap-3 px-3 py-2.5 ${
                          idx !== filteredPlayers.length - 1 ? 'border-b border-border' : ''
                        } ${isTopThree ? 'bg-warning/5' : ''}`}
                      >
                        {/* Rank */}
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center font-bold text-xs flex-shrink-0 ${
                          idx === 0 ? 'bg-warning text-white' :
                          idx === 1 ? 'bg-gray-400 text-white' :
                          idx === 2 ? 'bg-orange-400 text-white' :
                          'bg-surface-raised text-text-secondary'
                        }`}>
                          {idx + 1}
                        </div>

                        {/* Player Info */}
                        <div className="min-w-0 flex-1 flex items-center justify-between gap-2">
                          <button
                            onClick={() => navigate(`/player/${slugifyPlayerName(player.playerName)}`)}
                            className="font-bold text-sm text-text-primary truncate hover:underline text-left"
                          >
                            {player.playerName}
                          </button>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {player.teamLogoUrl ? (
                              <img
                                src={player.teamLogoUrl}
                                alt={player.teamShortName}
                                loading="lazy"
                                className="w-4 h-4 rounded-full object-contain flex-shrink-0"
                              />
                            ) : (
                              <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: player.teamColor }} />
                            )}
                            <span className="text-xs font-semibold text-text-secondary">{player.teamShortName}</span>
                          </div>
                        </div>

                        {/* Category Points */}
                        <div className="text-right flex-shrink-0">
                          <p className="font-bold text-lg text-text-primary">{catValue}</p>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {/* STATS TAB */}
          {activeTab === 'stats' && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div className="bg-surface border border-border rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-text-primary">{matchStats.total}</p>
                <p className="text-xs text-text-secondary font-medium mt-1">Total Matches</p>
              </div>
              <div className="bg-surface border border-border rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-success">{matchStats.completed}</p>
                <p className="text-xs text-text-secondary font-medium mt-1">Completed</p>
              </div>
              <div className="bg-surface border border-border rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-warning">{matchStats.live}</p>
                <p className="text-xs text-text-secondary font-medium mt-1">Live</p>
              </div>
              <div className="bg-surface border border-border rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-text-muted">{matchStats.pending}</p>
                <p className="text-xs text-text-secondary font-medium mt-1">Upcoming</p>
              </div>
              <div className="bg-surface border border-border rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-text-primary">{matchStats.teams}</p>
                <p className="text-xs text-text-secondary font-medium mt-1">Teams</p>
              </div>
              <div className="bg-surface border border-border rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-text-primary">{matchStats.players}</p>
                <p className="text-xs text-text-secondary font-medium mt-1">Players</p>
              </div>
              <div className="bg-surface border border-border rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-text-primary">{matchStats.sets}</p>
                <p className="text-xs text-text-secondary font-medium mt-1">Sets Played</p>
              </div>
              <div className="bg-surface border border-border rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-text-primary">{matchStats.points}</p>
                <p className="text-xs text-text-secondary font-medium mt-1">Total Points</p>
              </div>
              <div className="bg-surface border border-border rounded-xl p-4 text-center">
                <p className="text-xs text-text-muted mb-2">Tournament Duration</p>
                <p className="text-sm font-bold text-text-primary">
                  {tournament ? `${new Date(tournament.startDate).toLocaleDateString()} - ${new Date(tournament.endDate).toLocaleDateString()}` : '—'}
                </p>
              </div>

              {/* Top Team */}
              {topTeam && (
                <div className="bg-surface border border-border rounded-xl p-4 col-span-2 sm:col-span-1">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-text-secondary mb-2">Top Team</p>
                  <div className="flex items-center gap-3">
                    {topTeam.teamLogoUrl ? (
                      <img src={topTeam.teamLogoUrl} alt={topTeam.teamShortName} loading="lazy" className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <span className="w-10 h-10 rounded-full flex-shrink-0" style={{ backgroundColor: topTeam.teamColor }} />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-base font-bold truncate" style={{ color: topTeam.teamColor }}>{topTeam.teamShortName}</p>
                      <p className="text-xs text-text-secondary">{topTeam.points} points · {topTeam.wins}W {topTeam.losses}L</p>
                    </div>
                    <span className="ml-auto text-2xl flex-shrink-0">🥇</span>
                  </div>
                </div>
              )}

              {/* Top Player */}
              {topPlayer && (
                <div className="bg-surface border border-border rounded-xl p-4 col-span-2 sm:col-span-1">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-text-secondary mb-2">Top Player</p>
                  <div className="flex items-center gap-3">
                    {topPlayer.teamLogoUrl ? (
                      <img src={topPlayer.teamLogoUrl} alt={topPlayer.teamShortName} loading="lazy" className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <span className="w-10 h-10 rounded-full flex-shrink-0" style={{ backgroundColor: topPlayer.teamColor }} />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-base font-bold truncate" style={{ color: topPlayer.teamColor }}>{topPlayer.playerName}</p>
                      <p className="text-xs text-text-secondary">{topPlayer.totalStars} stars · {topPlayer.teamShortName}</p>
                    </div>
                    <span className="ml-auto text-2xl flex-shrink-0">🌟</span>
                  </div>
                </div>
              )}

              {/* Participating Teams */}
              <div className="bg-surface border border-border rounded-xl p-4 col-span-2 sm:col-span-3">
                <p className="text-[10px] font-bold uppercase tracking-wider text-text-secondary mb-3">Participating Teams ({teams.length})</p>
                <div className="flex flex-wrap gap-2">
                  {teams.map(team => (
                    <div
                      key={team.id}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-background border border-border"
                      style={{ borderLeftWidth: 3, borderLeftColor: team.color }}
                    >
                      {team.logoUrl ? (
                        <img src={team.logoUrl} alt={team.shortName} loading="lazy" className="w-5 h-5 rounded-full object-contain" />
                      ) : (
                        <span className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: team.color }} />
                      )}
                      <span className="text-xs font-semibold" style={{ color: team.color }}>{team.shortName}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recent Matches — latest first */}
              {liveMatches.length > 0 && (
                <div className="bg-surface border border-border rounded-xl p-4 col-span-2 sm:col-span-3">
                  <p className="text-[10px] text-text-secondary uppercase font-bold mb-3">Recent Matches</p>
                  <div className="space-y-2">
                    {[...liveMatches]
                      .sort((a, b) => b.matchNumber - a.matchNumber)
                      .slice(0, 8)
                      .map(m => {
                        const winner = m.winnerId
                          ? (m.winnerId === m.teamAId ? m.teamA : m.teamB)
                          : null;
                        const isLive = m.status === 'live' || m.status === 'set-won';
                        const isTimeout = m.status === 'timeout';
                        const isScheduled = m.status === 'scheduled';
                        const isCompleted = m.status === 'match-won';
                        const displayDate = m.date
                          ? new Date(m.date + 'T00:00:00').toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
                          : null;
                        return (
                          <div key={m.id} className="flex items-center justify-between py-1.5 border-b border-border last:border-0">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <span className="text-[10px] text-text-muted flex-shrink-0">#{m.matchNumber}</span>
                              <span className="text-xs font-semibold" style={{ color: m.teamA?.color }}>{m.teamA?.shortName}</span>
                              <span className="text-[10px] text-text-secondary">vs</span>
                              <span className="text-xs font-semibold" style={{ color: m.teamB?.color }}>{m.teamB?.shortName}</span>
                              {displayDate && <span className="text-[10px] text-text-muted flex-shrink-0">{displayDate}</span>}
                            </div>
                            {isLive && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-warning/10 text-warning flex-shrink-0">LIVE</span>}
                            {isTimeout && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-500 flex-shrink-0">TIMEOUT</span>}
                            {isScheduled && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-info/10 text-info flex-shrink-0">SCHEDULED</span>}
                            {isCompleted && winner && <span className="text-[10px] font-semibold text-success flex-shrink-0">{winner.shortName}</span>}
                          </div>
                        );
                      })}
                  </div>
                  {liveMatches.length > 8 && (
                    <p className="text-center text-[10px] text-text-muted mt-2">+{liveMatches.length - 8} more matches</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MatchesSubTabButton({ label, count, active, onClick }: { label: string; count: number; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-xs font-bold transition-colors ${
        active ? 'bg-primary text-white' : 'bg-surface border border-border text-text-secondary hover:text-text-primary'
      }`}
    >
      <span>{label}</span>
      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${active ? 'bg-white/20' : 'bg-surface-raised'}`}>{count}</span>
    </button>
  );
}

